import type { Subscription, SubscriptionCategory } from '../lib/subscriptionsApi';
import { isoDateTime } from './company';
import { testCompanyTag } from './companies';

export interface SubscriptionSeed {
  id: string;
  name: string;
  merchantPatterns: string[];
  category: SubscriptionCategory;
  expectedAmount: number | null;
}

export const SUBSCRIPTION_SEEDS: SubscriptionSeed[] = [
  {
    id: 'sub1',
    name: 'Claude AI (Anthropic)',
    merchantPatterns: ['ANTHROPIC*'],
    category: 'saas',
    expectedAmount: 100,
  },
  {
    id: 'sub2',
    name: 'Fireflies.ai',
    merchantPatterns: ['FIREFLIES*', 'FIREFLIES.AI*'],
    category: 'saas',
    expectedAmount: 19,
  },
  {
    id: 'sub3',
    name: 'SmartBill',
    merchantPatterns: ['NTP*smartbill*', 'NTP*SMARTBILL*'],
    category: 'saas',
    expectedAmount: 50,
  },
  { id: 'sub4', name: 'Slack', merchantPatterns: ['SLACK*'], category: 'saas', expectedAmount: 50 },
  {
    id: 'sub5',
    name: 'Miro',
    merchantPatterns: ['MIRO.COM*', 'MIRO*'],
    category: 'saas',
    expectedAmount: 40,
  },
  {
    id: 'sub6',
    name: 'Zoom',
    merchantPatterns: ['ZOOM.COM*', 'ZOOM.US*', 'ZOOM*'],
    category: 'saas',
    expectedAmount: 55,
  },
  {
    id: 'sub7',
    name: 'Vodafone',
    merchantPatterns: ['PayU*vodafone*', 'VODAFONE*'],
    category: 'telecom',
    expectedAmount: 150,
  },
  {
    id: 'sub8',
    name: 'OMV (Carburant)',
    merchantPatterns: ['OMV*'],
    category: 'fuel',
    expectedAmount: 300,
  },
  {
    id: 'sub9',
    name: 'Vercel',
    merchantPatterns: ['VERCEL*'],
    category: 'hosting',
    expectedAmount: 20,
  },
  {
    id: 'sub10',
    name: 'OpenAI',
    merchantPatterns: ['OPENAI*'],
    category: 'saas',
    expectedAmount: 20,
  },
  {
    id: 'sub11',
    name: 'Wispr',
    merchantPatterns: ['WISPR*'],
    category: 'saas',
    expectedAmount: 10,
  },
  {
    id: 'sub12',
    name: 'Sentry',
    merchantPatterns: ['SENTRY*', 'GETSENTRY*'],
    category: 'saas',
    expectedAmount: 26,
  },
  {
    id: 'sub13',
    name: 'GoDaddy',
    merchantPatterns: ['GODADDY*'],
    category: 'hosting',
    expectedAmount: 60,
  },
  {
    id: 'sub14',
    name: 'Higgsfield',
    merchantPatterns: ['HIGGSFIELD*'],
    category: 'saas',
    expectedAmount: 30,
  },
  {
    id: 'sub15',
    name: 'La Fantana',
    merchantPatterns: ['LA FANTANA*', 'LAFANTANA*'],
    category: 'office',
    expectedAmount: 200,
  },
  {
    id: 'sub16',
    name: 'Google Workspace',
    merchantPatterns: ['GOOGLE*Workspace*', 'Google*GSUITE*'],
    category: 'saas',
    expectedAmount: 60,
  },
  {
    id: 'sub17',
    name: 'Perplexity AI',
    merchantPatterns: ['WWW.PERPLEXITY*', 'PERPLEXITY*'],
    category: 'saas',
    expectedAmount: 20,
  },
];

const CREATED_AT = isoDateTime(-300, 10);
const UPDATED_AT = isoDateTime(-5, 14);

export function buildSubscriptions(): Subscription[] {
  return SUBSCRIPTION_SEEDS.map((s, i) => ({
    id: s.id,
    name: s.name,
    merchantPatterns: s.merchantPatterns,
    category: s.category,
    expectedAmount: s.expectedAmount,
    expectedCurrency: 'USD',
    frequency: 'monthly' as const,
    // Deterministic charge day spread across the month (cash-flow projection).
    paymentDay: ((i * 3) % 28) + 1,
    notes: null,
    isActive: true,
    supplierId: null,
    supplier: null,
    expenseCategoryId: null,
    ...testCompanyTag(s.id),
    createdAt: CREATED_AT,
    updatedAt: UPDATED_AT,
  }));
}

export function buildSubscription(id: string): Subscription | null {
  return buildSubscriptions().find((s) => s.id === id) ?? null;
}

// Settings-managed subscription category nomenclature. Duplicated locally
// (instead of importing DEFAULT_SUBSCRIPTION_CATEGORIES from
// lib/subscriptionsApi) to avoid a runtime import cycle:
// api -> mockRouter -> testData -> subscriptionsApi -> api.
export function buildSubscriptionCategories(): { code: string; label: string }[] {
  return [
    { code: 'saas', label: 'SaaS' },
    { code: 'utility', label: 'Utilitate' },
    { code: 'telecom', label: 'Telecom' },
    { code: 'fuel', label: 'Carburant' },
    { code: 'office', label: 'Birou' },
    { code: 'hosting', label: 'Hosting' },
    { code: 'other', label: 'Altele' },
  ];
}
